import type { ReactNode } from 'react';
import { Link, useLocation } from 'wouter';
import { HomeSwitcher } from './HomeSwitcher.js';
import { FeedbackWidget } from './FeedbackWidget.js';
import { useAuth } from '../context/AuthContext.js';
import { useT } from '../i18n/index.js';

export function AppShell({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const t = useT();
  const [location] = useLocation();

  const initials =
    (user?.displayName ?? user?.email ?? 'U')
      .split(/\s+|@/)
      .filter(Boolean)
      .map((s) => s[0]?.toUpperCase() ?? '')
      .slice(0, 2)
      .join('') || 'U';

  const isSettings = location.startsWith('/settings');

  return (
    <div className="min-h-screen flex flex-col">
      <header
        className="sticky top-0 z-30 flex items-center justify-between gap-3 px-4 py-3 border-b"
        style={{ backgroundColor: 'var(--ac-surface)', borderColor: 'var(--ac-border)' }}
      >
        <div className="flex items-center gap-3">
          <Link href="/" className="text-lg font-semibold" style={{ color: 'var(--ac-text)' }}>
            AetherCasa
          </Link>
          <HomeSwitcher />
        </div>
        <Link
          href="/settings/account"
          className="inline-flex items-center justify-center w-9 h-9 rounded-full text-sm font-medium"
          style={{ backgroundColor: 'var(--ac-accent)', color: 'white' }}
          aria-label="Account"
        >
          {initials}
        </Link>
      </header>

      <main className="flex-1 pb-20 md:pb-6">{children}</main>

      <nav
        className="fixed bottom-0 inset-x-0 z-30 md:hidden flex justify-around border-t py-2"
        style={{ backgroundColor: 'var(--ac-surface)', borderColor: 'var(--ac-border)' }}
      >
        <Link
          href="/"
          className="flex flex-col items-center text-xs py-1 px-4"
          style={{ color: location === '/' ? 'var(--ac-accent)' : 'var(--ac-text-muted)' }}
        >
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <path d="M3 11l9-8 9 8v10a2 2 0 0 1-2 2h-4v-7h-6v7H5a2 2 0 0 1-2-2z" />
          </svg>
          {t('nav.home')}
        </Link>
        <Link
          href="/settings"
          className="flex flex-col items-center text-xs py-1 px-4"
          style={{ color: isSettings ? 'var(--ac-accent)' : 'var(--ac-text-muted)' }}
        >
          <svg
            width="22"
            height="22"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
          >
            <circle cx="12" cy="12" r="3" />
            <path d="M19.4 15a1.7 1.7 0 0 0 .3 1.9l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.9-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 0 1-4 0v-.1a1.7 1.7 0 0 0-1-1.5 1.7 1.7 0 0 0-1.9.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.9 1.7 1.7 0 0 0-1.5-1H3a2 2 0 0 1 0-4h.1a1.7 1.7 0 0 0 1.5-1 1.7 1.7 0 0 0-.3-1.9l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.9.3H9a1.7 1.7 0 0 0 1-1.5V3a2 2 0 0 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.9-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.9V9a1.7 1.7 0 0 0 1.5 1H21a2 2 0 0 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z" />
          </svg>
          {t('nav.settings')}
        </Link>
      </nav>

      <FeedbackWidget />
    </div>
  );
}
